import { Location } from "@angular/common";
import {
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { Email, PoHeader } from "app/model/entities/entity-model";
import { PoStatusCode } from "app/model/enums/po-status-code";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { EmailService } from "app/shared/email.service";
import { PrintFns } from "app/shared/print-fns";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { PoPrintDialogComponent } from "./po-print-dialog.component";
import { DateFns } from "@/shared/date-fns";

interface FlatDetail {
  style: string;
  desc: string;
  featureChoicesExtract: string;
  qty: number;
  ddate: Date;
}

@Component({
  selector: "app-printable-po",
  templateUrl: "./printable-po.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PrintablePoComponent
  extends DomainBaseComponent
  implements OnDestroy
{
  @ViewChild("printableArea") printElementRef: ElementRef;
  poHeaderId: number;
  poHeader: PoHeader;
  // Need to calculate this here because PoDate and ExpectedShipDates are not YET set until the PO is marked complete.
  poDate: Date;
  email: Email;
  flatDetails: FlatDetail[];
  isAccountOwnedPo = false;
  getAddress = UtilFns.getAddress;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    private emailService: EmailService,
    private elementRef: ElementRef,
    private location: Location
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  ngOnDestroy(): void {
    // PrintFns.setPrintFn(null);
  }

  get isSent() {
    return (
      this.poHeader && this.poHeader.poStatusId >= PoStatusCode.OpenIncomplete
    );
  }

  get displayPoNumber(): string {
    return this.isAccountOwnedPo ? `INV-${this.poHeader.id}` : `${this.poHeader.id}`;
  }

  async updateFromContext() {
    this.poHeaderId = +this.route.snapshot.params["poHeaderId"];
    await this.dbQueryService.cacheAll();
    this.poHeader = await this.dbQueryService.getPoHeader(this.poHeaderId);
    if (this.poHeader == null) {
      this.isPageReady = true;
      return;
    }
    this.poDate = this.poHeader.poDate ?? new Date();

    const poDetails = await this.dbQueryService.getPoDetails(this.poHeaderId);
    this.isAccountOwnedPo = poDetails.some(pod => pod.joDetail?.joHeader?.isAccountOwnedOrder);

    const fmtCurrency = this.UtilFns.fmtCurrency;
    let details = poDetails.map((pod) => {
      const jo = pod.joDetail;
      let style: string, description: string;
      if (pod.manufacturerProductTypeConfig == null) {
        const pt = pod.product.productType;
        style = pt.style;
        description = pt.description;
      } else {
        style = pod.manufacturerProductTypeConfig.configName;
        description = pod.manufacturerProductTypeConfig.configDescription;
      }
      // Need to calculate this here because PoDate and ExpectedShipDates are not YET set until the PO is marked complete.

      const expectedShipDate = DateFns.dateAdd(
        this.poDate,
        this.poHeader.manufacturer.leadTimeDays,
        "days"
      );

      return {
        style: style,
        desc: description,
        featureChoicesExtract: pod.product.featureChoicesExtract,
        qty: pod.orderQty,
        ddate: expectedShipDate,
      };
    });

    // group the rows and sum the quantities
    const map = {};
    details = _.sortBy(
      details,
      (d) => d.style + d.featureChoicesExtract + d.ddate
    );
    details.forEach((d) => {
      const key = d.style + d.featureChoicesExtract + d.ddate;
      if (map[key]) {
        map[key].qty += d.qty;
      } else {
        map[key] = d;
      }
    });
    this.flatDetails = Object.values(map);

    this.isPageReady = true;

    setTimeout(() => {
      // if printing or headless browser, use print view
      const mediaQueryList = window.matchMedia("print");
      if (
        mediaQueryList.matches ||
        window.navigator.userAgent.includes("Headless")
      ) {
        this.preparePrint();
      } else {
        // get email status
        this.getPoEmailStatus();
      }
    }, 0);
  }

  async showPrintDialog() {
    const r = await DialogBaseComponent.open(PoPrintDialogComponent, this, {
      poHeader: this.poHeader,
      canPreview: false,
    });

    this.getPoEmailStatus();
    return <boolean>r;
  }

  preparePrint() {
    let el: HTMLElement =
      this.printElementRef && this.printElementRef.nativeElement;
    if (el == null) {
      el = this.elementRef && this.elementRef.nativeElement;
    }
    if (!el) {
      return;
    }
    PrintFns.printElement(el);
  }

  getPoEmailStatus() {
    this.emailService
      .getPoEmailStatus(this.poHeaderId)
      .then((e) => (this.email = e));
  }

  getOrderNote() {
    return (
      (this.poHeader.instructions
        ? this.poHeader.instructions +
          `

`
        : "") +
      `If not able to meet expected delivery dates, please contact our Purchasing Department immediately. Thank you.

Acceptance certifies compliance with federal and state regulations regarding equal opportunity without regard to race, religion, national origin, or sex. EEO/AA M/F/D/V`
    );
  }

  private gotoNavPath(navPath: string, id: any) {
    const fm = this.AgFns.addFilterClause({}, "id", "number", id);
    const urlSuffix = encodeURIComponent(JSON.stringify(fm));
    this.router.navigate([navPath], {
      queryParams: {
        fm: urlSuffix,
        key: id.toString(),
      },
    });
  }

  goToPo() {
    this.gotoNavPath("/purchase-orders", this.poHeaderId);
  }

  goBack() {
    this.location.back();
  }
}
